import type { ReactNode } from 'react'
import { Download, LoaderCircle, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { accountDayKey, startOfAccountDayFromKey } from '@/lib/accountTimezone'

type ReportShellProps = {
  title: string
  description: string
  from: Date
  to: Date
  onRangeChange: (range: { from: Date; to: Date }) => void
  loading: boolean
  error: string | null
  empty: boolean
  onRetry: () => void
  onExport: () => void
  children: ReactNode
}

/** Picker days are account-timezone days, matching the buckets the report renders. */
function inputDate(date: Date) {
  return accountDayKey(date)
}

export function ReportShell({
  title,
  description,
  from,
  to,
  onRangeChange,
  loading,
  error,
  empty,
  onRetry,
  onExport,
  children,
}: ReportShellProps) {
  const { t } = useTranslation('reports')

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground sm:text-3xl">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('shell.from')}
            <Input
              type="date"
              value={inputDate(from)}
              max={inputDate(to)}
              onChange={(event) => {
                const next = startOfAccountDayFromKey(event.target.value)
                if (next) onRangeChange({ from: next, to })
              }}
              className="h-10 w-[150px]"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('shell.to')}
            <Input
              type="date"
              value={inputDate(to)}
              min={inputDate(from)}
              onChange={(event) => {
                const next = startOfAccountDayFromKey(event.target.value)
                if (next) onRangeChange({ from, to: next })
              }}
              className="h-10 w-[150px]"
            />
          </label>
          <Button variant="outline" onClick={onExport} disabled={loading || empty}>
            <Download className="mr-2 h-4 w-4" />
            {t('shell.exportCsv')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-64 items-center justify-center rounded-xl border border-border bg-card">
          <LoaderCircle className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-xl border border-destructive/40 bg-card p-6 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={onRetry}>
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('shell.retry')}
          </Button>
        </div>
      ) : empty ? (
        <div className="flex min-h-64 items-center justify-center rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {t('shell.empty')}
        </div>
      ) : (
        children
      )}
    </div>
  )
}
